import React, { useContext, useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import { useKeepAliveGenSocket } from "../../hooks/useKeepAlive";
import { useUserPing } from "../../hooks/useUserPing";
import useXoltarNavigation from "../../hooks/useXoltarNav";
import { useCoreAvailability } from "../../hooks/validateCores";
import { useWebValidation } from "../../hooks/websiteValidatioHook";
import {
  createEventRequest,
  getLatestsUndoneEvents,
} from "../../Components/Api/EventApi";
import {
  addProlificUser,
  DuplicatedUser,
  InvalidParameters,
} from "../../Components/Api/headless";
import { getBackendInfo } from "../../Components/Api/ServiceApi";
import { createUser } from "../../Components/Api/UserApi";
import { WebSocketContext } from "../../Components/Context/WebSocketContext";
import { useAppDispatch, useAppSelector } from "../../redux-stores/hooks/hooks";
import {
  selectConversationConvID,
  selectConversationUser,
  setEventId,
  setUserId,
} from "../../redux-stores/reducers/ConversationState";
import { selectPage } from "../../redux-stores/reducers/PageState";
import {
  ProlificUser,
  selectProlificUser,
  selectRunProlific,
} from "../../redux-stores/reducers/ProlificExperiments";
import { selectQuestionnaireAnswer } from "../../redux-stores/reducers/QuestionerState";
import { addToast, clearToast } from "../../redux-stores/reducers/ToastState";

const sleep = (millis: number) => {
  return new Promise<void>((res, rej) => {
    setTimeout(() => {
      res();
    }, millis);
  });
};

export const LoadingConversation = () => {
  useWebValidation();
  useUserPing();
  const checkAvailableCore = useCoreAvailability();
  const navigation = useXoltarNavigation();
  const dispatch = useAppDispatch();
  const webSocket = useContext(WebSocketContext);
  useKeepAliveGenSocket(webSocket ?? null);
  const answers = useAppSelector(selectQuestionnaireAnswer);
  const userId = useAppSelector(selectConversationUser);
  const convId = useAppSelector(selectConversationConvID);
  const runProlific = useAppSelector(selectRunProlific);
  const prolificUser = useAppSelector(selectProlificUser);
  const pageInfo = useAppSelector(selectPage("Loading")) as any;

  const [stageMessage, setStageMessage] = useState("Loading");

  const [running, setRunning] = useState(false);

  const intervalCallback = React.useCallback((msg: string) => {
    if (pageInfo && pageInfo.texts) {
      const currentIndex = (pageInfo.texts as string[]).findIndex(
        (k) => k === msg
      );
      console.debug(currentIndex, msg);
      setStageMessage(
        pageInfo.texts[
          Math.min(
            (pageInfo.texts as string[]).length - 1,
            Math.max(-1, currentIndex) + 1
          )
        ]
      );
    }
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    const interval = setInterval(() => intervalCallback(stageMessage), 4_000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [stageMessage]);
  const scheduleEvent = React.useCallback(async () => {
    try {
      setRunning(true);
      if (running) return;
      const available = await checkAvailableCore();
      if (!available) navigation("Intro");
      console.debug("Saving user info");
      let user = { userId: userId, slug: userId };

      user = await createUser(answers["name"], answers);
      user.userId && dispatch(setUserId(user.userId));
      if (!user.userId) {
        navigation("Error");
        return;
      }
      // if (!user.userId) {
      //   console.error("Unable to create or retrieve the user");
      //   console.debug("Error occurred while talking to the server (1)");
      //   return;
      // }

      console.debug("Checking that every thing is ok");
      const oldEvent = await getLatestsUndoneEvents(user.userId);
      let pUser: ProlificUser | null | undefined;

      if (prolificUser && runProlific) {
        try {
          pUser = await addProlificUser(prolificUser);
        } catch (error) {
          if (error instanceof DuplicatedUser) {
            console.warn("User already exsists");
            pUser = prolificUser;
            console.debug(pUser);
          } else if (error instanceof InvalidParameters) {
            console.error("Server recived invalid data");
            throw error;
          } else {
            console.error(error);
            throw error;
          }
        }
      }
      let event: string | undefined =
        oldEvent && oldEvent.event ? oldEvent.event.eventId : undefined;

      try {
        if (!oldEvent || !oldEvent.event) {
          event = await createEventRequest(user.userId, answers, convId);
          if (!event)
            console.debug("Error occurred while talking to the server (2)");
        }
      } catch (error) {
        navigation("Error", new URLSearchParams({ type: "noConv" }));
      }

      console.debug("Looking for agent");
      let currentEventStatus: Awaited<
        ReturnType<typeof getLatestsUndoneEvents>
      >;
      dispatch(
        addToast({
          bg: "Info",
          header: "Agent Update",
          body: `conversation with id-${event}`,
          duration: 2_500,
          timestamp: Date.now(),
        })
      );
      dispatch(
        addToast({
          bg: "Info",
          header: "Agent Update",
          body: `agent reading your data`,
          duration: 2_500,
          timestamp: Date.now(),
        })
      );
      do {
        currentEventStatus = await getLatestsUndoneEvents(user.userId);
        if (currentEventStatus && currentEventStatus.status === "FETCHED") {
          console.debug("Agent is now reading you information");
        }
        await sleep(5000);
      } while (!currentEventStatus || currentEventStatus.status !== "LOADED");
      dispatch(setEventId(currentEventStatus.event.eventId));
      console.debug("Agent will be ready in a moment");
      // dispatch(
      //   addToast({
      //     bg: "Info",
      //     header: "Agent receive your data",
      //     body: `${event}`,
      //     duration: 2_500,
      //     timestamp: Date.now(),
      //   })
      // );
      setStageMessage("Redirecting to video chat");
      dispatch(
        addToast({
          bg: "Info",
          header: "Agent Update",
          body: `Moving to chat`,
          duration: 2_500,
          timestamp: Date.now(),
        })
      );
      await sleep(2_500);
      dispatch(clearToast());

      navigation("Conversation");
    } catch (error) {
      console.warn(error);
      console.debug("Unknown Error ");
    }
    // eslint-disable-next-line
  }, [running, setRunning, stageMessage, setStageMessage]);

  useEffect(() => {
    console.debug("Effect", running);
    scheduleEvent().catch((e) => {
      console.error("Uncaught error");
    });

    getBackendInfo().then((b) => {
      b.conv && webSocket && webSocket.connectTo(b.conv);
    });
    // eslint-disable-next-line
  }, []);
  return (
    <Container
      style={{
        display: "flex",
        justifyContent: "center",
      }}
    >
      <div
        className="my-auto "
        style={{
          height: "50vh",
        }}
      >
        <div
          style={{
            height: "100%",
            width: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <img
            height="100%"
            alt="Loading"
            src={require("../../assets/loading_trans.gif")}
          />
          <br />
          <div>
            <h4 style={{ textAlign: "center" }}>{stageMessage}</h4>
          </div>
        </div>
      </div>
    </Container>
  );
};
